import * as React from 'react';
import { ReactElement, useState } from 'react';
import BasicModal from 'components/common/modal/BasicModal';
import styled from 'styled-components';
import { TextField, Typography } from '@material-ui/core';
import { ColorText } from 'components/plansales/billing/detail/invoice/InvoiceTableRenderer';
import DatePicker from 'components/common/searchfilter/contents/DatePicker';
import { getLaterUtcDateDash, getUtcDateDash } from 'modules/utils/time';
import { putEndDate, putStartDate } from 'api/backorder';
import Button from 'components/common/Button';
import ErrorModal from 'components/common/modal/ErrorModal';
import { AxiosError } from 'axios';
import { ErrorResult } from 'types/api';

interface Props {
  type: 'start' | 'end';
  subscriptionId: number;
  targetDate: string | null;
  // Earliest selectable date; today (UTC) still wins when this is earlier or missing
  minDate?: string | null;
  onClose: () => void;
}

const EditDateModal = ({
  type,
  subscriptionId,
  targetDate,
  minDate,
  onClose,
}: Props): ReactElement => {
  const dateTitle = type === 'start' ? 'Start Date' : 'End Date';
  const today = new Date();
  const minSelectableDate = minDate ? getLaterUtcDateDash(today, minDate) : getUtcDateDash(today);
  const [commentValue, setCommentValue] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(targetDate ?? '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDatePickerValueChanged, setIsDatePickerValueChanged] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<{ code: number | string; message: string } | null>(
    null,
  );

  const onCloseModal = (): void => {
    setCommentValue('');
    setSelectedDate(targetDate ?? '');
    onClose();
  };

  const onConfirm = async (): Promise<void> => {
    setIsLoading(true);
    const commonParams = { subscriptionId, comment: commentValue };
    try {
      type === 'start'
        ? await putStartDate({ startDate: selectedDate, ...commonParams })
        : await putEndDate({ endDate: selectedDate, ...commonParams });
      window.location.reload();
    } catch (e) {
      const error = e as AxiosError<ErrorResult>;
      if (!error.response?.data) {
        setErrorStatus({
          code: error.code ?? 500,
          message: error.message,
        });
        return;
      }
      setErrorStatus({
        code: error.response.data.resultCode,
        message: error.response.data.resultMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <BasicModal
        open={true}
        onClose={onCloseModal}
        title={`Edit ${dateTitle}`}
        actionButton={
          <>
            <Button variant={'text'} color={'default'} onClick={onCloseModal}>
              Close
            </Button>
            <Button
              variant={'text'}
              color={'primary'}
              onClick={onConfirm}
              disabled={!commentValue}
              isLoading={isLoading}
            >
              Confirm
            </Button>
          </>
        }
      >
        <ContentsWrapper>
          <ContentTypo variant="subtitle1">
            Are you sure you want to edit the {dateTitle} to
          </ContentTypo>
          <DatePickerWrapper>
            <DatePicker
              format={'MM/dd/yyyy'}
              onChange={selectedDate => {
                setSelectedDate(getUtcDateDash(selectedDate));
                if (isDatePickerValueChanged) return;
                setIsDatePickerValueChanged(true);
              }}
              minDate={minSelectableDate}
              value={selectedDate}
            />
          </DatePickerWrapper>
          ?
        </ContentsWrapper>
        <CommentWrapper>
          <Typography>
            Comment <ColorText color={'red'}> *</ColorText>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant={'outlined'}
            value={commentValue}
            onChange={e => {
              setCommentValue(e.target.value);
            }}
            inputProps={{ maxLength: 500 }}
          />
        </CommentWrapper>
      </BasicModal>
      {errorStatus && (
        <ErrorModal content={errorStatus.message} onClose={() => setErrorStatus(null)} isOpen />
      )}
    </>
  );
};

export default EditDateModal;

const CommentWrapper = styled.div`
  margin: ${props => props.theme.spacing(3)}px 0px;
`;
const ContentTypo = styled(Typography)`
  margin-right: ${props => props.theme.spacing(3)}px;
  margin-bottom: ${props => props.theme.spacing(10)}px;
`;
const ContentsWrapper = styled.div`
  display: flex;
  align-items: start;
`;
const DatePickerWrapper = styled.div`
  max-width: 150px;
`;
